import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Input } from '@reservamos/elements';
import { EXCHANGE_TYPEAHEAD_CHANGE } from 'constants/ActionTypes';
import useHybridSearchEnabled from 'hooks/useHybridSearchEnabled';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { parseSearchDefaultPlaceType } from 'utils/search/searchDefaultPlaceType';
import Options from './TypeaheadOptionsContainer';
import './Typeahead';
import ExchangeContext from '../context';

const ExchangeTypeahead = ({
  isDisabled,
  label,
  name,
  onChange,
  onKeyUp,
  value,
  url,
  hasError,
  errorMessage,
  handleBlur,
}) => {
  const dispatch = useDispatch();
  const hybridSearchEnabled = useHybridSearchEnabled();
  const features = useWhitelabelFeatures();
  // Hybrid search needs concrete terminals; otherwise the tenant base type applies.
  const placeType = hybridSearchEnabled
    ? 'terminal'
    : parseSearchDefaultPlaceType(features.SEARCH_DEFAULT_PLACE_TYPE);
  const [showItems, setShowItems] = useState(false);
  const containerRef = useRef(null);

  const handleOptionSelect = useCallback(
    (display, slug) => {
      setShowItems(false);
      dispatch({ type: EXCHANGE_TYPEAHEAD_CHANGE, name, display, slug });
    },
    [dispatch, name],
  );
  const contextValue = useMemo(() => ({ handleOptionSelect }), [handleOptionSelect]);

  const handleClickOutside = (event) => {
    if (containerRef && !containerRef.current.contains(event.target)) {
      setShowItems(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <ExchangeContext.Provider value={contextValue}>
        <Input
          autocomplete="off"
          id={name}
          label={label}
          name={name}
          onChange={(e) => onChange(e.target.value)}
          onKeyUp={(e) => onKeyUp(name, e)}
          value={value}
          readOnly={isDisabled}
          hasError={hasError}
          errorMessage={errorMessage}
          onBlur={handleBlur}
          borderRadius="XL"
        />
        <Options
          placeType={placeType}
          url={url}
          showItems={showItems}
          setShowItems={setShowItems}
        />
      </ExchangeContext.Provider>
    </div>
  );
};

const propTypes = {
  isDisabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  onKeyUp: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  handleBlur: PropTypes.func,
};

const defaultProps = {
  isDisabled: false,
};

ExchangeTypeahead.propTypes = propTypes;
ExchangeTypeahead.defaultProps = defaultProps;

export default ExchangeTypeahead;
